import React, { useState, useRef } from 'react';
import { View, Text, Image, Pressable, StyleSheet, Modal, ScrollView } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

interface ImageListModalProps {
    visible: boolean;
    onClose: () => void;
    images: Array<{uri: string}>;
    isEditing?: boolean;
    onDeleteImage?: (index: number) => void;
    onAddImage?: (files?: FileList) => void;
    onReorderImages?: (fromIndex: number, toIndex: number) => void;
}

const ImageListModal: React.FC<ImageListModalProps> = ({
    visible,
    onClose,
    images,
    isEditing = false,
    onDeleteImage,
    onAddImage,
    onReorderImages
}) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const mainScrollViewRef = useRef<ScrollView>(null);
    const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const scrollToImage = (index: number) => {
        setSelectedImageIndex(index);
        const imageHeight = 400;
        const marginBottom = 16;
        const totalHeight = imageHeight + marginBottom;
        const scrollPosition = index * totalHeight;
        
        mainScrollViewRef.current?.scrollTo({
            y: scrollPosition,
            animated: true
        });
    };

    const handleLongPress = (index: number) => {
        if (!isEditing) return;
        setDraggedIndex(index);
    };

    const handlePressIn = (index: number) => {
        if (!isEditing) return;
        dragTimeoutRef.current = setTimeout(() => {
            setDraggedIndex(index);
        }, 500); // 500ms delay for long press
    };

    const handlePressOut = () => {
        if (dragTimeoutRef.current) {
            clearTimeout(dragTimeoutRef.current);
            dragTimeoutRef.current = null;
        }
    };

    const handleDrop = () => {
        if (draggedIndex !== null && dragOverIndex !== null && onReorderImages) {
            onReorderImages(draggedIndex, dragOverIndex);
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleFileDrop = (event: any) => {
        event.preventDefault();
        const files = event.dataTransfer?.files;
        if (files && files.length > 0 && onAddImage) {
            const fileUrls = Array.from(files).map(file => URL.createObjectURL(file as Blob));
            onAddImage();
        }
    };

    const handleFileSelect = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true;
        
        input.onchange = (event) => {
            const files = (event.target as HTMLInputElement).files;
            if (files && files.length > 0 && onAddImage) {
                // Pass the selected files to the parent component
                onAddImage(files);
            }
        };
        
        input.click();
    };

    const handleDragOver = (event: any) => {
        event.preventDefault();
    };

    const handleThumbnailDragOver = (index: number) => {
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleThumbnailPress = (index: number) => {
        if (draggedIndex !== null) {
            handleDrop();
        } else {
            scrollToImage(index);
        }
    };

    return (
        <Modal visible={visible} transparent={true} onRequestClose={onClose}>
            <View style={styles.modalOverlay}>
                <View style={styles.container}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Pressable onPress={onClose} style={styles.backButton}>
                            <MaterialCommunityIcons name="arrow-left" size={24} color="#333" />
                        </Pressable>
                        <Text style={styles.headerTitle}>Photo tour</Text>
                        <View style={styles.headerActions}>
                            <Pressable style={styles.headerButton}>
                                <MaterialCommunityIcons name="share-variant" size={20} color="#333" />
                            </Pressable>
                            <Pressable style={styles.headerButton}>
                                <MaterialCommunityIcons name="bookmark-outline" size={20} color="#333" />
                            </Pressable>
                        </View>
                    </View>

                    {/* Thumbnail Navigation */}
                    <ScrollView 
                        horizontal 
                        showsHorizontalScrollIndicator={false}
                        style={styles.thumbnailContainer}
                        contentContainerStyle={styles.thumbnailContent}
                    >
                        {images.map((image, index) => (
                            <Pressable 
                                key={index}
                                style={[
                                    styles.thumbnailWrapper,
                                    selectedImageIndex === index && styles.selectedThumbnail,
                                    draggedIndex === index && styles.draggedThumbnail,
                                    dragOverIndex === index && styles.dragOverThumbnail
                                ]}
                                onPress={() => handleThumbnailPress(index)}
                                onLongPress={() => handleLongPress(index)}
                                onPressIn={() => handlePressIn(index)}
                                onPressOut={handlePressOut}
                                onHoverIn={() => handleThumbnailDragOver(index)}
                                onHoverOut={() => setDragOverIndex(null)}
                            >
                                <Image source={image} style={styles.thumbnail} />
                                {isEditing && (
                                    <View style={styles.thumbnailOverlay}>
                                        <MaterialCommunityIcons name="drag" size={16} color="white" />
                                    </View>
                                )}
                                {isEditing && (
                                    <Pressable 
                                        style={styles.thumbnailDeleteButton}
                                        onPress={() => onDeleteImage?.(index)}
                                    >
                                        <MaterialCommunityIcons name="close" size={12} color="white" />
                                    </Pressable>
                                )}
                            </Pressable>
                        ))}
                        
                        {isEditing && (
                            <Pressable 
                                style={styles.addThumbnail} 
                                onPress={handleFileSelect}
                            >
                                <MaterialCommunityIcons name="camera-plus" size={24} color="#ccc" />
                            </Pressable>
                        )}
                    </ScrollView>

                    {/* Main Image Display */}
                    <ScrollView 
                        ref={mainScrollViewRef}
                        style={styles.mainImageContainer}
                        contentContainerStyle={styles.mainImageContent}
                        showsVerticalScrollIndicator={false}
                    >
                        {images.map((image, index) => (
                            <View key={index} style={styles.mainImageWrapper}>
                                <Image source={image} style={styles.mainImage} />
                                {isEditing && (
                                    <Pressable 
                                        style={styles.deleteButton}
                                        onPress={() => onDeleteImage?.(index)}
                                    >
                                        <MaterialCommunityIcons name="close" size={16} color="white" />
                                    </Pressable>
                                )}
                            </View>
                        ))}
                        
                        {isEditing && (
                            <Pressable 
                                style={styles.addImageCard}
                                onPress={handleFileSelect}
                            >
                                <View style={styles.addImageContent}>
                                    <MaterialCommunityIcons name="camera-plus" size={32} color="#ccc" />
                                    <Text style={styles.addImageText}>Add photos</Text>
                                    <Text style={styles.addImageSubtext}>Drag and drop or click to select</Text>
                                </View>
                            </Pressable>
                        )}
                    </ScrollView>
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
    },
    container: {
        flex: 1,
        backgroundColor: 'white',
        width: '100%',
        height: '100%',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 16,
        paddingTop: 60,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    backButton: {
        padding: 8,
        borderRadius: 20,
        backgroundColor: '#f8f9fa',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: '#333',
    },
    headerActions: {
        flexDirection: 'row',
        gap: 12,
    },
    headerButton: {
        padding: 8,
        borderRadius: 20,
        backgroundColor: '#f8f9fa',
    },
    thumbnailContainer: {
        maxHeight: 100,
        borderBottomWidth: 1,
        borderBottomColor: '#f0f0f0',
    },
    thumbnailContent: {
        paddingHorizontal: 16,
        paddingVertical: 12,
        gap: 12,
    },
    thumbnailWrapper: {
        width: 80,
        height: 60,
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 2,
        borderColor: 'transparent',
        position: 'relative',
    },
    selectedThumbnail: {
        borderColor: '#333',
    },
    draggedThumbnail: {
        borderColor: '#007bff',
        borderWidth: 2,
        borderStyle: 'dashed',
    },
    dragOverThumbnail: {
        borderColor: '#28a745',
        borderWidth: 2,
        borderStyle: 'dashed',
    },
    thumbnail: {
        width: '100%',
        height: '100%',
        resizeMode: 'contain',
    },
    thumbnailOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        borderRadius: 8,
        justifyContent: 'center',
        alignItems: 'center',
    },
    thumbnailDeleteButton: {
        position: 'absolute',
        top: 4,
        right: 4,
        backgroundColor: 'rgba(220, 53, 69, 0.9)',
        borderRadius: 10,
        width: 20,
        height: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    addThumbnail: {
        width: 80,
        height: 60,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: '#e0e0e0',
        borderStyle: 'dashed',
        justifyContent: 'center',
        alignItems: 'center',
    },
    mainImageContainer: {
        flex: 1,
        paddingHorizontal: 16,
        paddingVertical: 16,
    },
    mainImageContent: {
        alignItems: 'center',
    },
    mainImageWrapper: {
        width: '35%',
        height: 400,
        borderRadius: 25,
        overflow: 'hidden',
        marginBottom: 16,
        position: 'relative',
    },
    mainImage: {
        width: '100%',
        height: '100%',
        resizeMode: 'contain',
    },
    deleteButton: {
        position: 'absolute',
        top: 12,
        right: 12,
        backgroundColor: 'rgba(220, 53, 69, 0.9)',
        borderRadius: 20,
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 4,
        elevation: 3,
    },
    addImageCard: {
        width: '69%',
        height: 180,
        borderRadius: 16,
        borderWidth: 3,
        borderColor: '#e0e0e0',
        borderStyle: 'dashed',
        backgroundColor: '#fafafa',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
    },
    addImageContent: {
        alignItems: 'center',
        gap: 8,
    },
    addImageText: {
        color: '#999',
        fontSize: 14,
        fontWeight: '500',
        textAlign: 'center',
    },
    addImageSubtext: {
        color: '#bbb',
        fontSize: 12,
        textAlign: 'center',
    },
});

export default ImageListModal; 